import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  XIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CalendarIcon,
  InformationCircleIcon,
  LightningBoltIcon,
} from '@heroicons/react/outline';
import { functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { TriggerRmsDocumentGeneration } from '../../utils/TriggerRmsDocumentGeneration';

/**
 * TriageDecisionDialog
 * --------------------
 * Purpose:
 *   A step-by-step wizard dialog that allows clinicians to make triage decisions
 *   after reviewing the triage proforma. Designed to reduce visual overwhelm by
 *   presenting one focused step at a time.
 *
 * Wizard Steps:
 *   1. Pathway Allocation - Select assessment pathway (Autism, ADHD, Combined, Complex)
 *   2. Priority Level - Select clinician-determined priority/risk level
 *   3. Recommended Next steps - Enter recommended next steps
 *   4. Appointment (Optional) - Enter appointment date/time and notes if booked
 *
 * Business context:
 *   After reviewing all 7 triage forms submitted by the legal guardian, the clinician
 *   meets with the wellbeing practitioner to discuss the case. The clinician then
 *   makes a triage decision that determines the patient's assessment pathway.
 *
 * Data model:
 *   The triage decision is stored on the patient record at `triageDecision`:
 *   {
 *     pathwayAllocation: 'autism' | 'adhd' | 'combined' | 'complex',
 *     priorityLevel: 'urgent' | 'high' | 'medium' | 'standard',
 *     assignedClinician: string,
 *     recommendedNextSteps: string,
 *     appointmentDetails: { dateTime: string | null, notes: string | null },
 *     decidedBy: { clinicianId: string, clinicianName: string },
 *     decidedAt: Timestamp,
 *     version: number
 *   }
 *
 * Props:
 *   - isOpen: boolean - Controls dialog visibility
 *   - onOpenChange: (open: boolean) => void - Callback when dialog should open/close
 *   - orgId: string - Organisation ID
 *   - patientRecordId: string - The CYP's patient record ID
 *   - patientName: string - Patient's name for display
 *   - existingDecision: object | null - Pre-populate if editing an existing decision
 *   - riskStratification: object | null - Automated risk stratification from triage forms
 *   - availableClinicians: array - List of clinicians that can be assigned
 *     [{ clinicianId: string, firstName: string, lastName: string, userType: string }]
 *   - onSuccess: () => void - Optional callback after successful submission
 */

// The four assessment pathways a patient can be allocated to
const PATHWAY_OPTIONS = [
  {
    id: 'autism',
    label: 'Autism',
    description: 'Assessment for Autism Spectrum Condition',
  },
  {
    id: 'adhd',
    label: 'ADHD',
    description: 'Assessment for Attention Deficit Hyperactivity Disorder',
  },
  {
    id: 'combined',
    label: 'Combined Autism and ADHD',
    description: 'Assessment for both Autism and ADHD',
  },
  {
    id: 'complex',
    label: 'Complex',
    description: 'Complex presentation requiring specialist assessment',
  },
];

/**
 * Priority level options for clinician determination.
 * These match the automated risk stratification tiers but are independently selected.
 * Color scheme based on severity:
 *   - Urgent (Tier 1): Red
 *   - High (Tier 2): Orange
 *   - Medium (Tier 3): Yellow
 *   - Standard (Tier 4): Green
 */
const PRIORITY_OPTIONS = [
  {
    id: 'urgent',
    label: 'Urgent Priority',
    description: 'Immediate, life-threatening safety concerns requiring rapid escalation',
    tier: 1,
    color: '#dc2626', // red-600
    bgColor: '#fef2f2', // red-50
    borderColor: '#fecaca', // red-200
  },
  {
    id: 'high',
    label: 'High Priority',
    description: 'High risk of rapid deterioration; prioritised for early review',
    tier: 2,
    color: '#ea580c', // orange-600
    bgColor: '#fff7ed', // orange-50
    borderColor: '#fed7aa', // orange-200
  },
  {
    id: 'medium',
    label: 'Medium Priority',
    description: 'Significant, compounding risks; early-intervention pathway recommended',
    tier: 3,
    color: '#854d0e', // yellow-800
    bgColor: '#fffbeb', // amber-50
    borderColor: '#fde68a', // amber-200
  },
  {
    id: 'standard',
    label: 'Standard Priority',
    description: 'Significant difficulties but no acute safety concerns; suitable for routine assessment',
    tier: 4,
    color: '#16a34a', // green-600
    bgColor: '#f0fdf4', // green-50
    borderColor: '#bbf7d0', // green-200
  },
];

// Accept/Reject options shown as the first wizard step.
// The clinician must decide whether to accept the child onto the waiting list
// before proceeding to pathway allocation (accept) or providing a rationale (reject).
const ACCEPT_REJECT_OPTIONS = [
  {
    id: 'yes',
    label: 'Yes - Accept onto Waiting List',
    description: 'The referral meets criteria. Proceed to assign a pathway and clinician.',
  },
  {
    id: 'no',
    label: 'No - Reject Referral',
    description: 'The referral does not meet criteria. You will be asked to provide a rationale.',
  },
];

export default function TriageDecisionDialog({
  isOpen,
  onOpenChange,
  orgId,
  patientRecordId,
  patientName,
  pathway,                  // C8: 'adhd'|'asd' for combined referrals (per-pathway triage); undefined otherwise
  existingDecision,
  triageReviews = [],       // full array of previous reviews from patientRecord.triageReviews
  riskStratification,
  availableClinicians = [],
  onSuccess,
}) {
  // ─────────────────────────────────────────────────────────────────────────────
  // State Management
  // ─────────────────────────────────────────────────────────────────────────────

  // Wizard step (1-based). Step 1 is always the Accept/Reject question.
  // If accepted: steps 2-5 are Pathway, Priority, Recommended Next Steps.
  // If rejected: step 2 is the Rejection Rationale, then submit.
  const [currentStep, setCurrentStep] = useState(1);

  // UI state machine: 'input' → 'loading' → 'success' | 'error'
  const [state, setState] = useState('input');
  const [errorMessage, setErrorMessage] = useState(null);

  // Step 1: Accept/Reject decision.
  // null = not yet chosen, true = accept, false = reject.
  const [accepted, setAccepted] = useState(null);

  // Step 2 (reject path only): free-text rationale explaining why the referral
  // does not meet criteria. Required before the rejection can be submitted.
  const [rationale, setRationale] = useState('');

  // Form field state (only used when accepted === true)
  const [pathwayAllocation, setPathwayAllocation] = useState('');
  const [priorityLevel, setPriorityLevel] = useState('');
  const [assignedClinicianId, setAssignedClinicianId] = useState('');
  const [recommendedNextSteps, setRecommendedNextSteps] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [appointmentNotes, setAppointmentNotes] = useState('');

  // AI preview state — used by the "Preview AI Output" lightning bolt in step 4.
  // Only available in dev/staging so clinicians can see what the LLM will produce
  // for the next-steps paragraph of the outcome letter before submitting.
  const [aiPreviewText, setAiPreviewText] = useState('');
  const [aiPreviewLoading, setAiPreviewLoading] = useState(false);
  const [aiPreviewError, setAiPreviewError] = useState(null);
  const [aiPreviewExpanded, setAiPreviewExpanded] = useState(false);

  // Derive the assigned clinician's full name from the ID for display and submission
  const assignedClinicianData = useMemo(() => {
    if (!assignedClinicianId) return null;
    const clinician = availableClinicians.find((c) => c.clinicianId === assignedClinicianId);
    if (!clinician) return null;
    return {
      clinicianId: clinician.clinicianId,
      clinicianName: `${clinician.firstName || ''} ${clinician.lastName || ''}`.trim(),
    };
  }, [assignedClinicianId, availableClinicians]);

  // The wizard has a branching structure: acceptance flows through 5 steps while
  // rejection only needs 2 (the accept/reject question + a rationale text box).
  // Before the clinician has chosen, we show "Step 1 of ?" so default to 5.
  const totalSteps = useMemo(() => {
    if (accepted === false) return 2;  // reject path: Accept/Reject → Rationale
    return 5;                          // accept path: Accept/Reject → Pathway → Priority → Details → Appointment
  }, [accepted]);

  // Determine whether this is the 1st or 2nd review of this referral. The backend
  // enforces guards (e.g. different clinician for 2nd review), but the dialog uses
  // this context to display informational banners and success messages.
  //
  // IMPORTANT: This is stored as state (not a memo) and only set when the dialog
  // opens. This prevents real-time Firestore updates (which fire after the Cloud
  // Function writes the new review) from flipping the success message while the
  // dialog is still visible — e.g. the 1st rejection success screen incorrectly
  // showing "Both clinicians rejected" because triageReviews now has 1 entry.
  const [reviewContext, setReviewContext] = useState({ isSecondReview: false, firstReviewerName: null, firstReviewRationale: null });

  // Ref keeps the latest triageReviews value available without triggering effects.
  // The "snapshot on open" effect below reads from this ref so it always gets the
  // current prop value at the moment isOpen flips to true, but doesn't re-run
  // when Firestore pushes real-time updates to triageReviews while the dialog is open.
  const triageReviewsRef = useRef(triageReviews);
  triageReviewsRef.current = triageReviews;

  // ─────────────────────────────────────────────────────────────────────────────
  // Form Initialization
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Resets all form fields and wizard state to initial values.
   * Note: priorityLevel is intentionally NOT pre-filled from existing decision
   * to ensure the clinician makes a fresh determination each time.
   */
  const resetForm = useCallback(() => {
    setCurrentStep(1);
    setAccepted(null);
    setRationale('');
    setPathwayAllocation('');
    setPriorityLevel(''); // Always start fresh - no prefill
    setAssignedClinicianId('');
    setRecommendedNextSteps('');
    setAppointmentDate('');
    setAppointmentTime('');
    setAppointmentNotes('');
    setState('input');
    setErrorMessage(null);
    setAiPreviewText('');
    setAiPreviewError(null);
    setAiPreviewLoading(false);
    setAiPreviewExpanded(false);
  }, []);

  /**
   * Initializes form fields when dialog opens or when editing an existing decision.
   * If existingDecision is provided, pre-populates most fields with the saved values.
   * 
   * IMPORTANT: priorityLevel is intentionally NOT pre-filled to ensure the clinician
   * makes a fresh determination each time they review the case.
   */
  useEffect(() => {
    // Guard: don't re-initialise when the dialog is already showing the
    // success or loading screen. After submission, the backend writes the
    // triage decision to Firestore which triggers an onSnapshot update in
    // the parent, causing `existingDecision` to change. Without this guard,
    // the effect would reset the dialog back to step 1 and blow away the
    // success / Done screen before the user can click "Done".
    if (state === 'success' || state === 'loading') return;

    if (isOpen && existingDecision) {
      setPathwayAllocation(existingDecision.pathwayAllocation || '');
      setPriorityLevel(''); // Always fresh - clinician must re-determine priority
      
      // Try to match existing assigned clinician to available clinicians by ID first,
      // then fall back to name matching if the decision has assignedClinicianId
      const existingId = existingDecision.assignedClinicianId || '';
      const existingName = existingDecision.assignedClinician || '';
      
      if (existingId && availableClinicians.some((c) => c.clinicianId === existingId)) {
        setAssignedClinicianId(existingId);
      } else if (existingName) {
        // Try to find by name match (for backwards compatibility with old decisions)
        const match = availableClinicians.find(
          (c) => `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase() === existingName.toLowerCase()
        );
        setAssignedClinicianId(match?.clinicianId || '');
      } else {
        setAssignedClinicianId('');
      }
      
      setRecommendedNextSteps(existingDecision.recommendedNextSteps || '');

      // Parse appointment details if present
      const appt = existingDecision.appointmentDetails || {};
      if (appt.dateTime) {
        // Split ISO datetime into date and time parts
        const dt = new Date(appt.dateTime);
        if (!isNaN(dt.getTime())) {
          setAppointmentDate(dt.toISOString().split('T')[0]);
          setAppointmentTime(dt.toTimeString().slice(0, 5));
        }
      } else {
        setAppointmentDate('');
        setAppointmentTime('');
      }
      setAppointmentNotes(appt.notes || '');
      setCurrentStep(1);
      setState('input');
    } else if (isOpen && !existingDecision) {
      // Reset form when opening fresh (no existing decision)
      resetForm();
    }
  }, [isOpen, existingDecision, availableClinicians, resetForm, state]);

  // Snapshot the review context exactly once when the dialog opens. We read from
  // the triageReviewsRef (which holds the latest prop value) at open-time and
  // freeze it so that real-time Firestore updates (triggered by the Cloud Function
  // writing back) don't change the UI while the clinician is still looking at the
  // success/error screen.
  useEffect(() => {
    if (isOpen) {
      const reviews = Array.isArray(triageReviewsRef.current) ? triageReviewsRef.current : [];
      const isSecondReview = reviews.length === 1 && reviews[0].accepted === false;
      // The second reviewer sees what the first one decided and wrote, so that
      // work is not wasted, but never who they were: masking the name and
      // professional title avoids hierarchy bias (RMS-283, 6 Aug). Showing the
      // notes was confirmed by Tom King on 23 Sept, superseding the earlier
      // position that withheld them.
      const firstReviewerName = isSecondReview ? 'Another clinician' : null;
      const firstReviewRationale = isSecondReview
        ? (reviews[0].conclusion || reviews[0].rationale || null)
        : null;
      setReviewContext({ isSecondReview, firstReviewerName, firstReviewRationale });
    }
  }, [isOpen]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Step Validation
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Validates whether the current step's required fields are filled in.
   *
   * Step numbering differs by path:
   *   Step 1 (both): Accept/Reject must be chosen
   *   --- Accept path ---
   *   Step 2: Pathway must be selected
   *   Step 3: Priority level must be selected
   *   Step 4: Recommended next steps text must be non-empty
   *   Step 5: All fields are optional (appointment)
   *   --- Reject path ---
   *   Step 2: Rationale text must be non-empty
   */
  const isCurrentStepValid = useMemo(() => {
    if (currentStep === 1) {
      return accepted !== null; // must choose yes or no
    }

    // Reject path: step 2 is the rationale
    if (accepted === false) {
      return currentStep === 2 ? Boolean(rationale.trim()) : false;
    }

    // Accept path: steps 2-5
    switch (currentStep) {
      case 2:
        return Boolean(pathwayAllocation);
      case 3:
        return Boolean(priorityLevel);
      case 4:
        return Boolean(recommendedNextSteps.trim());
      case 5:
        return true; // appointment is optional
      default:
        return false;
    }
  }, [currentStep, accepted, rationale, pathwayAllocation, priorityLevel, recommendedNextSteps]);

  /**
   * Determines if we're editing an existing decision vs creating a new one.
   */
  const isEditing = Boolean(existingDecision);

  // ─────────────────────────────────────────────────────────────────────────────
  // Navigation Handlers
  // ─────────────────────────────────────────────────────────────────────────────

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, totalSteps]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      // When going back from the reject rationale step (step 2 on reject path)
      // to step 1, reset the accept/reject choice so the clinician can re-decide.
      if (currentStep === 2 && accepted === false) {
        setAccepted(null);
        setRationale('');
      }
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep, accepted]);

  // ─────────────────────────────────────────────────────────────────────────────
  // AI Preview (dev/staging only)
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Calls the lightweight rmsPreviewNextSteps Cloud Function to generate an AI
   * paragraph from the clinician's recommended-next-steps notes. The result is
   * shown in an expandable accordion below the text area — it is NOT persisted.
   */
  const handlePreviewNextSteps = useCallback(async () => {
    if (!recommendedNextSteps.trim()) return;
    setAiPreviewLoading(true);
    setAiPreviewError(null);
    setAiPreviewText('');
    setAiPreviewExpanded(true);

    try {
      const fn = httpsCallable(functions, 'rmsPreviewNextSteps', { timeout: 120000 });
      const result = await fn({
        recommendedNextSteps: recommendedNextSteps.trim(),
      });
      setAiPreviewText(result.data?.generatedText || '(No output returned)');
    } catch (err) {
      console.error('AI preview failed:', err);
      setAiPreviewError(err.message || 'Failed to generate preview');
    } finally {
      setAiPreviewLoading(false);
    }
  }, [recommendedNextSteps]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Form Submission
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Handles form submission by calling the backend Cloud Function.
   * The CF will validate permissions, save the decision, and create an audit trail.
   *
   * The payload shape differs depending on whether the clinician accepted or rejected:
   * - Accepted: includes pathway, priority, clinician, next steps, and appointment.
   * - Rejected: includes only the boolean flag and the rationale text.
   */
  const handleSubmit = async () => {
    setState('loading');
    setErrorMessage(null);

    try {
      const submitTriageDecision = httpsCallable(functions, 'rmsSubmitTriageDecision');

      // Build payload – the backend validates fields conditionally based on `accepted`.
      const payload = {
        orgId,
        patientRecordId,
        accepted: Boolean(accepted),
        // C8: required for combined referrals so the decision lands on the right
        // pathway; ignored server-side for single-concern referrals.
        ...(pathway ? { pathway } : {}),
      };

      if (accepted) {
        // ── Acceptance payload: pathway + priority + clinician + next steps + optional appointment ──
        payload.pathwayAllocation = pathwayAllocation;
        payload.priorityLevel = priorityLevel;
        payload.assignedClinicianId = assignedClinicianData?.clinicianId || '';
        payload.assignedClinician = assignedClinicianData?.clinicianName || '';
        payload.recommendedNextSteps = recommendedNextSteps.trim();

        // Construct appointment details if date is provided
        if (appointmentDate) {
          const timeStr = appointmentTime || '09:00';
          const dateTimeStr = `${appointmentDate}T${timeStr}:00`;
          payload.appointmentDetails = {
            dateTime: dateTimeStr,
            notes: appointmentNotes.trim() || null,
          };
        }
      } else {
        // ── Rejection payload: rationale only ──
        payload.rationale = rationale.trim();
      }

      const result = await submitTriageDecision(payload);

      if (result?.data?.success) {
        setState('success');

        // Trigger AI letter generation for final outcomes only:
        //   - Outcome 1: referral accepted (accepted === true)
        //   - Outcome 3: 2nd clinician also rejects (reviewContext.isSecondReview && accepted === false)
        // Outcome 2 (1st rejection, awaiting 2nd opinion) does NOT trigger generation.
        const isFinalOutcome = accepted || (accepted === false && reviewContext.isSecondReview);
        if (isFinalOutcome) {
          TriggerRmsDocumentGeneration({ orgId, patientRecordId, pathway });
        }

        if (onSuccess) {
          onSuccess({ isFinalOutcome });
        }
      } else {
        throw new Error(result?.data?.message || 'Failed to save triage decision');
      }
    } catch (error) {
      console.error('Error submitting triage decision:', error);
      setErrorMessage(
        error?.message || 'Something went wrong. Please try again or contact support.'
      );
      setState('error');
    }
  };

  /**
   * Resets the dialog state when closing, so reopening starts fresh.
   */
  const handleOpenChange = (open) => {
    if (!open) {
      // Reset state after a short delay to avoid visual jump during close animation
      setTimeout(() => {
        resetForm();
      }, 200);
    }
    onOpenChange(open);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Step Content Rendering
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Renders the content for Step 1: Accept onto Waiting List? Yes / No
   *
   * This is always the first step. The clinician must decide whether to accept
   * the referral (proceeding to pathway allocation) or reject it (providing a rationale).
   */
  const renderAcceptRejectStep = () => (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1">
          Accept onto Waiting List?
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Based on your triage review, decide whether this referral should be accepted onto the
          assessment waiting list or rejected.
        </p>
      </div>

      {/* Informational banner for 2nd reviewers — shown when the 1st clinician
          already rejected this referral and a second opinion is being sought. */}
      {reviewContext.isSecondReview && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-1">
          <div className="flex items-start gap-2">
            <InformationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">Second Opinion Requested</p>
              <p className="text-xs text-amber-800 mt-0.5">
                <strong>{reviewContext.firstReviewerName}</strong> previously reviewed this referral and decided not to accept.
                You are providing a second opinion, and your decision is the final one. If you also
                reject, the referral will not be accepted onto the pathway at this stage.
              </p>
              {/* Their written reasoning, so the first review's work carries
                  forward. Their identity stays masked above. */}
              {reviewContext.firstReviewRationale ? (
                <div className="mt-2 rounded border border-amber-200 bg-white p-2">
                  <p className="text-xs font-semibold text-amber-900">
                    What the first reviewer wrote
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-xs text-gray-800">
                    {reviewContext.firstReviewRationale}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {ACCEPT_REJECT_OPTIONS.map((option) => {
          const isSelected =
            (option.id === 'yes' && accepted === true) ||
            (option.id === 'no' && accepted === false);
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => setAccepted(option.id === 'yes')}
              className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 ${
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                {/* Radio indicator */}
                <span
                  className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                    isSelected
                      ? 'border-primary bg-primary'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {isSelected && <span className="w-2 h-2 rounded-full bg-white" />}
                </span>
                <div>
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-xs text-gray-500">{option.description}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  /**
   * Renders the content for Step 2 (reject path only): Rejection Rationale.
   *
   * The clinician must explain why the referral does not meet criteria.
   * This rationale will be visible to other clinicians on the dashboard.
   */
  const renderRejectionRationaleStep = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Rejection Rationale</h3>
        <p className="text-xs text-gray-500 mb-4">
          Please explain why this referral does not meet the criteria for acceptance onto the
          waiting list. This will be recorded and visible to other clinicians reviewing the case.
        </p>
      </div>

      <textarea
        rows={5}
        value={rationale}
        onChange={(e) => setRationale(e.target.value)}
        placeholder="Describe your clinical rationale for rejecting this referral..."
        className="w-full p-3 border-2 border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
      />

      {/* Guidance note — different messaging for 1st vs 2nd reviewer */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <InformationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            {reviewContext.isSecondReview
              ? 'This is the second rejection. Submitting will discharge the referral.'
              : 'This referral will be sent for a second opinion by another clinician. If they also reject it, the referral will not be accepted onto the pathway at this stage.'}
          </p>
        </div>
      </div>
    </div>
  );

  /**
   * Renders the content for Pathway Allocation step (step 2 on accept path).
   */
  const renderPathwayStep = () => (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1">
          Select Assessment Pathway
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Choose the pathway that best matches this patient's presentation
        </p>
      </div>

      <div className="space-y-2">
        {PATHWAY_OPTIONS.map((pathway) => {
          const isSelected = pathwayAllocation === pathway.id;
          return (
            <button
              key={pathway.id}
              type="button"
              onClick={() => setPathwayAllocation(pathway.id)}
              className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 ${
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                {/* Radio indicator */}
                <span
                  className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                    isSelected
                      ? 'border-primary bg-primary'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {isSelected && <span className="w-2 h-2 rounded-full bg-white" />}
                </span>
                <div>
                  <div className="font-medium text-gray-900">{pathway.label}</div>
                  <div className="text-xs text-gray-500">{pathway.description}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  /**
   * Renders the content for the Priority Level step (step 3 on accept path).
   * Shows the automated risk stratification result (if available) as reference,
   * but requires the clinician to make their own independent determination.
   */
  const renderPriorityStep = () => {
    // Extract automated risk data for display
    const hasAutomatedRisk = riskStratification?.success && riskStratification?.tier;
    const automatedLabel = riskStratification?.tierLabel;

    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-1">
            Select Priority Level
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            Based on your clinical review, determine the appropriate priority for this case
          </p>
        </div>

        {/* Show automated risk stratification if available */}
        {hasAutomatedRisk && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
            <div className="flex items-start gap-2">
              <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Automated Risk Assessment: {automatedLabel}
                </p>
                <p className="text-xs text-blue-700 mt-0.5">
                  This is based on the triage form responses. Please make your own clinical determination below.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Priority level options */}
        <div className="space-y-2">
          {PRIORITY_OPTIONS.map((option) => {
            const isSelected = priorityLevel === option.id;

            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setPriorityLevel(option.id)}
                className="w-full text-left p-3 rounded-lg border-2 transition-all duration-200"
                style={{
                  borderColor: isSelected ? option.color : '#e5e7eb',
                  backgroundColor: isSelected ? option.bgColor : 'white',
                }}
              >
                <div className="flex items-center gap-3">
                  {/* Radio indicator with priority color */}
                  <span
                    className="flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all"
                    style={{
                      borderColor: isSelected ? option.color : '#d1d5db',
                      backgroundColor: isSelected ? option.color : 'white',
                    }}
                  >
                    {isSelected && <span className="w-2 h-2 rounded-full bg-white" />}
                  </span>
                  <div className="flex-grow">
                    <div className="flex items-center gap-2">
                      <span
                        className="font-medium"
                        style={{ color: isSelected ? option.color : '#111827' }}
                      >
                        {option.label}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">{option.description}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /**
   * Renders the content for the Recommended Next Steps step (step 4 on accept path).
   * Uses a dropdown select for assigned clinician populated from availableClinicians prop.
   */
  const renderClinicalDetailsStep = () => (
    <div className="space-y-4">
      {/* Recommended Next Steps */}
      <div>
        <label
          htmlFor="recommendedNextSteps"
          className="block text-sm font-semibold text-gray-800 mb-1"
        >
          Recommended Next Steps <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Clinical recommendations for the patient&apos;s journey
        </p>
        <textarea
          id="recommendedNextSteps"
          rows={4}
          value={recommendedNextSteps}
          onChange={(e) => setRecommendedNextSteps(e.target.value)}
          placeholder="Describe the recommended next steps for this patient..."
          className="w-full p-3 border-2 border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
        />
      </div>

      {/* AI next-steps review (TD-46) — exposed in UAT, not just dev. The clinician
          reviews the AI-drafted paragraph that will appear in the outcome letter
          before submitting the triage decision. */}
      <div className="rounded-lg border border-gray-200 overflow-hidden">
          {/* Accordion header with lightning bolt trigger */}
          <button
            type="button"
            onClick={() => {
              if (!aiPreviewText && !aiPreviewLoading && !aiPreviewError) {
                handlePreviewNextSteps();
              } else {
                setAiPreviewExpanded((prev) => !prev);
              }
            }}
            disabled={!recommendedNextSteps.trim() || aiPreviewLoading}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left bg-gray-50 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {aiPreviewLoading ? (
              <svg className="w-4 h-4 animate-spin text-primary flex-shrink-0" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
              </svg>
            ) : (
              <LightningBoltIcon className="w-4 h-4 text-primary flex-shrink-0" />
            )}
            <span className="font-medium text-gray-700 flex-1">
              {aiPreviewLoading
                ? 'Generating AI preview...'
                : aiPreviewText
                  ? 'AI Letter Preview'
                  : 'Preview AI output for outcome letter'}
            </span>
            {(aiPreviewText || aiPreviewError) && !aiPreviewLoading && (
              <ChevronDownIcon
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                  aiPreviewExpanded ? 'rotate-180' : ''
                }`}
              />
            )}
          </button>

          {/* Expandable content area */}
          {aiPreviewExpanded && (
            <div className="border-t border-gray-200 px-4 py-3">
              {aiPreviewError && (
                <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">
                  {aiPreviewError}
                </div>
              )}
              {aiPreviewText && (
                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-white rounded-md border border-gray-100 p-3">
                  {aiPreviewText}
                </div>
              )}
              {aiPreviewText && (
                <button
                  type="button"
                  onClick={handlePreviewNextSteps}
                  disabled={!recommendedNextSteps.trim() || aiPreviewLoading}
                  className="mt-2 flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LightningBoltIcon className="w-3.5 h-3.5" />
                  Regenerate
                </button>
              )}
            </div>
          )}
        </div>

      {/* Signposting & end options — placeholder structure for UAT (TD-47).
          Scaffolds the post-decision signposting (local support services, what
          happens next, onward contact) so UAT can review the intended shape;
          final copy/links and any persistence are to be confirmed. */}
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-1">Signposting &amp; next steps</h4>
        <p className="text-xs text-gray-500 mb-2">Placeholder for UAT — final content to be confirmed.</p>
        <ul className="list-disc list-inside text-xs text-gray-500 space-y-1">
          <li>Information about local support services</li>
          <li>What happens after this decision</li>
          <li>How to get in touch / onward contact</li>
        </ul>
      </div>
    </div>
  );

  /**
   * Renders the content for the Appointment Details step (step 5 on accept path, optional).
   */
  const renderAppointmentStep = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Appointment Details</h3>
        <p className="text-xs text-gray-500 mb-4">
          If an appointment has already been booked, enter the details below. You can skip
          this step if no appointment is scheduled yet.
        </p>
      </div>

      {/* Date and Time row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="appointmentDate"
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            Date
          </label>
          <input
            id="appointmentDate"
            type="date"
            value={appointmentDate}
            onChange={(e) => setAppointmentDate(e.target.value)}
            className="w-full p-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label
            htmlFor="appointmentTime"
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            Time
          </label>
          <input
            id="appointmentTime"
            type="time"
            value={appointmentTime}
            onChange={(e) => setAppointmentTime(e.target.value)}
            className="w-full p-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>


      {/* Version info if editing */}
      {isEditing && existingDecision?.version && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mt-2">
          <p className="text-xs text-blue-800">
            <strong>Note:</strong> You are updating an existing triage decision (version{' '}
            {existingDecision.version}). The previous decision will be preserved in the audit
            history.
          </p>
        </div>
      )}
    </div>
  );

  /**
   * Renders the current step content based on currentStep and the accept/reject branch.
   *
   * Step 1 is always Accept/Reject. After that, the rendering depends on the branch:
   *   Accept path → Pathway (2) → Priority (3) → Recommended Next Steps (4) → Appointment (5)
   *   Reject path → Rationale (2)
   */
  const renderStepContent = () => {
    // Step 1: always accept/reject
    if (currentStep === 1) return renderAcceptRejectStep();

    // Reject path: step 2 is the rationale
    if (accepted === false) {
      return currentStep === 2 ? renderRejectionRationaleStep() : null;
    }

    // Accept path: steps 2-5
    switch (currentStep) {
      case 2:
        return renderPathwayStep();
      case 3:
        return renderPriorityStep();
      case 4:
        return renderClinicalDetailsStep();
      case 5:
        return renderAppointmentStep();
      default:
        return null;
    }
  };

  /**
   * Gets the human-readable title for the current step, shown in the progress indicator.
   */
  const getStepTitle = () => {
    if (currentStep === 1) return 'Accept / Reject';

    if (accepted === false) {
      return currentStep === 2 ? 'Rejection Rationale' : '';
    }

    switch (currentStep) {
      case 2:
        return 'Pathway Allocation';
      case 3:
        return 'Priority Level';
      case 4:
        return 'Recommended Next Steps';
      case 5:
        return 'Appointment';
      default:
        return '';
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        {/* Backdrop overlay */}
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />

        {/* Dialog content container - more compact max-width */}
        <Dialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[85vh] w-[90vw] max-w-md bg-white rounded-xl shadow-lg z-50 overflow-hidden flex flex-col">
          {/* Header with close button */}
          <div className="flex items-start justify-between p-5 pb-0">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {state === 'success'
                  ? accepted
                    ? 'Referral Accepted'
                    : 'Rejection Recorded'
                  : state === 'error'
                  ? 'Error'
                  : isEditing
                  ? 'Update Triage Decision'
                  : 'Complete Triage Review'}
              </Dialog.Title>
              {state === 'input' && patientName && (
                <Dialog.Description className="text-sm text-gray-500 mt-0.5">
                  {patientName}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close dialog"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Progress indicator (only shown during input state) */}
          {state === 'input' && (
            <div className="px-5 pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">
                  Step {currentStep} of {totalSteps}: {getStepTitle()}
                </span>
              </div>
              <div className="flex h-1.5 rounded-full bg-gray-200">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-300 ease-in-out"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Scrollable content area */}
          <div className="flex-grow overflow-y-auto p-5" style={{ maxHeight: '55vh' }}>
            {/* ─── Input State (Wizard Steps) ─────────────────────────────────── */}
            {state === 'input' && renderStepContent()}

            {/* ─── Loading State ─────────────────────────────────────────────── */}
            {state === 'loading' && (
              <div className="flex flex-col items-center justify-center py-8">
                {/* Spinner animation */}
                <div className="relative w-10 h-10 mb-3">
                  <div className="absolute inset-0 border-4 border-primary/20 rounded-full" />
                  <div className="absolute inset-0 border-4 border-transparent border-t-primary rounded-full animate-spin" />
                </div>
                <p className="text-sm text-gray-600">Saving triage decision...</p>
              </div>
            )}

            {/* ─── Success State ─────────────────────────────────────────────── */}
            {state === 'success' && (
              <div className="flex flex-col items-center justify-center py-4">
                {/* Success icon */}
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                  <CheckCircleIcon className="w-8 h-8 text-green-600" />
                </div>

                {accepted ? (
                  <>
                    {/* ── Accept success message ─────────────────────────────── */}
                    <p className="text-base font-medium text-gray-900 text-center mb-2">
                      Referral accepted
                    </p>
                    <p className="text-sm text-gray-600 text-center max-w-xs mb-3">
                      {patientName || 'The patient'} has been allocated to the{' '}
                      <strong>
                        {PATHWAY_OPTIONS.find((p) => p.id === pathwayAllocation)?.label}
                      </strong>{' '}
                      pathway and added to the waiting list.
                    </p>

                    {/* Summary */}
                    <div className="w-full bg-gray-50 rounded-lg border border-gray-200 p-3 text-left">
                      <div className="space-y-1.5 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Priority:</span>
                          <span
                            className="font-medium px-2 py-0.5 rounded text-xs"
                            style={{
                              backgroundColor: PRIORITY_OPTIONS.find((p) => p.id === priorityLevel)?.bgColor,
                              color: PRIORITY_OPTIONS.find((p) => p.id === priorityLevel)?.color,
                            }}
                          >
                            {PRIORITY_OPTIONS.find((p) => p.id === priorityLevel)?.label}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Assigned to:</span>{' '}
                          <span className="text-gray-900">{assignedClinicianData?.clinicianName || 'Unknown'}</span>
                        </div>
                        {appointmentDate && (
                          <div className="flex items-center gap-1.5">
                            <CalendarIcon className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-900">
                              {new Date(appointmentDate).toLocaleDateString('en-GB', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                              {appointmentTime && ` at ${appointmentTime}`}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* ── Reject success message — contextual for 1st vs 2nd review ── */}
                    <p className="text-base font-medium text-gray-900 text-center mb-2">
                      {reviewContext.isSecondReview ? 'Referral Not Accepted' : 'Decision Recorded'}
                    </p>
                    <p className="text-sm text-gray-600 text-center max-w-xs mb-3">
                      {reviewContext.isSecondReview
                        ? 'Both clinicians have rejected this referral. It has not been accepted onto the pathway at this stage.'
                        : 'Your rejection has been recorded. The referral will be sent to another clinician for a second opinion.'}
                    </p>

                    <div className="w-full bg-gray-50 rounded-lg border border-gray-200 p-3 text-left">
                      <div className="text-sm">
                        <span className="text-gray-500">Your rationale:</span>
                        <p className="text-gray-900 mt-1 whitespace-pre-wrap">{rationale}</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ─── Error State ───────────────────────────────────────────────── */}
            {state === 'error' && (
              <div className="flex flex-col items-center justify-center py-4">
                {/* Error icon */}
                <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-3">
                  <ExclamationCircleIcon className="w-8 h-8 text-red-600" />
                </div>
                <p className="text-base font-medium text-gray-900 text-center mb-2">
                  Something went wrong
                </p>
                <p className="text-sm text-red-600 text-center max-w-xs mb-3">{errorMessage}</p>
                <button
                  type="button"
                  onClick={() => setState('input')}
                  className="text-sm text-primary hover:text-primary/80 underline"
                >
                  Try again
                </button>
              </div>
            )}
          </div>

          {/* Footer with navigation/action buttons */}
          <div className="flex justify-between gap-3 p-5 pt-3 border-t border-gray-100">
            {state === 'input' && (
              <>
                {/* Left button: Back (if not on step 1) or Cancel (on step 1) */}
                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium flex items-center"
                  >
                    <ChevronLeftIcon className="w-4 h-4 mr-1" />
                    Back
                  </button>
                ) : (
                  <Dialog.Close asChild>
                    <button
                      type="button"
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </Dialog.Close>
                )}

                {/* Right button: Next (if not on last step) or Submit (on last step) */}
                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!isCurrentStepValid}
                    className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center ${
                      isCurrentStepValid
                        ? 'bg-primary text-white hover:bg-primary/90'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Next
                    <ChevronRightIcon className="w-4 h-4 ml-1" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                  >
                    {accepted === false
                      ? 'Submit Rejection'
                      : isEditing
                      ? 'Update Decision'
                      : 'Save Decision'}
                  </button>
                )}
              </>
            )}

            {state === 'loading' && (
              <button
                type="button"
                disabled
                className="ml-auto px-4 py-2 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed"
              >
                Please wait...
              </button>
            )}

            {(state === 'success' || state === 'error') && (
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="ml-auto px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                >
                  {state === 'success' ? 'Done' : 'Close'}
                </button>
              </Dialog.Close>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
